import { useLazyQuery } from '@apollo/client';
import {
  AptunoApolloLink,
  BaseQueryResponse2,
  ResourceNotFoundError,
  Response,
  ResponseApolloErrorMapping,
} from '@contexts/Shared';
import {
  CreditStudy,
  CreditStudyQueryActions,
  UserCreditStudyQuery,
  UserInformation,
} from '../../../domain';
import {
  CreditStudyResponseDto,
  ProvidedUserInformationResponseDto,
  UserInformationDto,
} from '../dtos';
import { getProvidedUserInformation } from '../schemas';
import {
  GetUserCreditStudySchema,
  UserCreditStudyResponse,
} from '../schemas/queries/get-user-credit-study.query';
import { useCallback, useMemo } from 'react';

export const GqlCreditStudyQueryActions: CreditStudyQueryActions = {
  useGetProvidedUserInformation: (): BaseQueryResponse2<
    void,
    UserInformation
  > => {
    const [action, { data, loading, error, refetch }] =
      useLazyQuery<ProvidedUserInformationResponseDto>(
        getProvidedUserInformation,
        {
          errorPolicy: 'all',
          context: { clientName: AptunoApolloLink.CREDIT_STUDY },
        }
      );

    const recall = useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    const result: Response<UserInformation> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : !data.getProvidedUserInformation?.value?.userId
          ? new Response.Failure(
              new ResourceNotFoundError('providedUserInformation')
            )
          : new Response.Success(
              UserInformationDto.asDomain(data.getProvidedUserInformation.value)
            ),
      [data, error]
    );

    return {
      loading,
      recall,
      result,
      get: () => action(),
    };
  },

  useGetUserCreditStudy: (): BaseQueryResponse2<
    UserCreditStudyQuery,
    CreditStudy
  > => {
    const [action, { data, loading, error, refetch }] =
      useLazyQuery<UserCreditStudyResponse>(GetUserCreditStudySchema, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

    const result: Response<CreditStudy> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : data.getUserCreditStudy.value?.id
          ? new Response.Success(
              CreditStudyResponseDto.asDomain(data.getUserCreditStudy.value)
            )
          : new Response.Failure(new ResourceNotFoundError('userCreditStudy')),
      [data, error]
    );

    const recall = useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    return {
      loading,
      result,
      recall,
      get: () => {
        action({
          variables: undefined,
        });
      },
    };
  },
};
